/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output, signal,
} from '@angular/core';
import { statusMaxChars } from '../../../../../status-limit';
import {
  AttachmentMode, StatusAttachmentDraft,
} from './status-attachment-picker.component';

export interface StatusDraft {
  content: string;
  attached_url: string | null;
}

// Status text + attachment form shared by the composer and the inline status editor.
@Component({
  selector: 'ut-community-status-form',
  templateUrl: './community-status-form.component.html',
  styleUrls: ['./community-status-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CommunityStatusFormComponent implements OnInit {
  @Input() content = '';
  @Input() attachedUrl: string | null = null;
  @Input() submitLabel = 'Share status';
  @Input() cancellable = false;
  @Input() busy = false;
  // user_type of the status author; the limit follows the author, not the editor.
  @Input() authorUserType: string | null = null;
  @Output() submitted = new EventEmitter<StatusDraft>();
  @Output() cancelled = new EventEmitter<void>();

  readonly inputId = `statusInput${Math.random().toString(36).slice(2)}`;

  attachment = signal<StatusAttachmentDraft | null>(null);
  pickerMode = signal<AttachmentMode | null>(null);

  constructor(
    @Inject('UploadService') private UploadService: any,
    @Inject('AlertService') private AlertService: any,
    private host: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    if (this.attachedUrl) {
      this.attachment.set({
        label: this.attachedUrl.substring(this.attachedUrl.lastIndexOf('/') + 1),
        value: this.attachedUrl,
        icon: 'insert_drive_file',
      });
    }
  }

  get maxChars(): number {
    return statusMaxChars(this.authorUserType);
  }

  get remainingChars(): number {
    return this.maxChars - (this.content?.length || 0);
  }

  togglePicker(mode: AttachmentMode): void {
    this.pickerMode.set(this.pickerMode() === mode ? null : mode);
  }

  // Closing or finishing with the picker returns focus to the button that opened it.
  closePicker(): void {
    const mode = this.pickerMode();
    this.pickerMode.set(null);
    this.focusLater(`[data-picker="${mode}"]`);
  }

  // The chip's remove button disappears with the chip, so focus goes back to the text.
  removeAttachment(): void {
    this.setAttachment(null);
    this.focusLater(`#${this.inputId}`);
  }

  setAttachment(attachment: StatusAttachmentDraft | null): void {
    // Delete a file uploaded earlier from this form when it is replaced or removed.
    const previous = this.attachment();
    if (previous?.id) {
      this.UploadService.delete(previous.id);
    }
    const mode = this.pickerMode();
    this.attachment.set(attachment);
    this.pickerMode.set(null);
    if (attachment && mode) this.focusLater(`[data-picker="${mode}"]`);
  }

  submit(): void {
    const attachedUrl = this.attachment()?.value || null;
    if (!this.content.trim() && !attachedUrl) {
      this.AlertService.addDanger('Can\'t post empty status!');
      return;
    }
    this.submitted.emit({ content: this.content, attached_url: attachedUrl });
  }

  private focusLater(selector: string): void {
    setTimeout(() => this.host.nativeElement.querySelector<HTMLElement>(selector)?.focus());
  }

  // Called by the parent after a successful post.
  reset(): void {
    this.content = '';
    this.attachment.set(null);
    this.pickerMode.set(null);
  }
}
